import React, { useEffect, useState } from 'react';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { studioAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import {
  Sparkles, Wand2, Clapperboard, Eye, ArrowRight, CheckCircle2, Loader2,
} from 'lucide-react';

/**
 * KI-Studio teaser. The studio (photo enhancement + image-to-video clips,
 * every result approved by the user, monthly allowance + top-up credits)
 * is in development; this page shows what is coming with one real
 * example and collects early-tester sign-ups. Swap it for the studio
 * itself at launch and drop `soon` from the nav item in AppLayout.
 */
const MEDIA_BASE = `${process.env.REACT_APP_BACKEND_URL}/uploads`;
const DEMO = {
  photo: `${MEDIA_BASE}/demo-content/landing/dachwohnung-salzburg/01_terrasse.jpg`,
  clip: `${MEDIA_BASE}/showcase/studio/salzburg_terrasse.mp4?v=1`,
};

const FEATURES = [
  { icon: Wand2, title: 'studio.feature1Title', body: 'studio.feature1Body' },
  { icon: Clapperboard, title: 'studio.feature2Title', body: 'studio.feature2Body' },
  { icon: Eye, title: 'studio.feature3Title', body: 'studio.feature3Body' },
];

export default function StudioTeaserPage() {
  const { t } = useTranslation();
  const [interested, setInterested] = useState(null); // null = loading
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    studioAPI.getInterest()
      .then((r) => setInterested(!!r.data?.interested))
      .catch(() => setInterested(false));
  }, []);

  const signUp = async () => {
    setSaving(true);
    try {
      await studioAPI.registerInterest();
      setInterested(true);
      toast.success(t('studio.ctaToast'));
    } catch (e) {
      toast.error(t('studio.ctaError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8 space-y-8">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f6edd8] border border-[#c7a869] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#8f6f3a]">
            <Sparkles className="w-3.5 h-3.5" />
            {t('studio.kicker')}
          </span>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold text-[#23211a]">
            {t('studio.title')}
          </h1>
          <p className="mt-2 text-[#6f6a5c] text-sm sm:text-base leading-relaxed">
            {t('studio.subtitle')}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] md:items-start">
          {/* Demo: the listing photo next to the clip made from it */}
          <section className="rounded-2xl border border-[#e3dccb] bg-[#fffdf7] p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-[#23211a] mb-3">{t('studio.demoTitle')}</h2>
            <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 sm:gap-3">
              <figure className="m-0">
                <img
                  src={DEMO.photo}
                  alt={t('studio.demoPhoto')}
                  className="w-full aspect-[9/16] object-cover rounded-xl border border-[#e3dccb]"
                  loading="lazy"
                />
                <figcaption className="mt-2 text-xs text-[#6f6a5c] text-center">{t('studio.demoPhoto')}</figcaption>
              </figure>
              <ArrowRight className="w-5 h-5 text-[#8f6f3a]" aria-hidden="true" />
              <figure className="m-0">
                <video
                  src={DEMO.clip}
                  poster={DEMO.photo}
                  className="w-full aspect-[9/16] object-cover rounded-xl border border-[#e3dccb] bg-[#efe9db]"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                />
                <figcaption className="mt-2 text-xs font-medium text-[#1f4d3a] text-center">{t('studio.demoClip')}</figcaption>
              </figure>
            </div>
            <p className="mt-3 text-xs text-[#8a8474] leading-relaxed">{t('studio.demoNote')}</p>
          </section>

          <div className="space-y-6">
            <ul className="space-y-4">
              {FEATURES.map(({ icon: Icon, title, body }) => (
                <li key={title} className="flex gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#e9efe7] text-[#1f4d3a]">
                    <Icon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-[#23211a]">{t(title)}</h3>
                    <p className="mt-0.5 text-sm text-[#6f6a5c] leading-relaxed">{t(body)}</p>
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-sm text-[#4a463b] border-l-2 border-[#c7a869] pl-3 leading-relaxed">
              {t('studio.pricingNote')}
            </p>

            <section className="rounded-2xl bg-[#1f4d3a] p-5 text-white">
              <h2 className="text-lg font-semibold">{t('studio.ctaTitle')}</h2>
              {interested ? (
                <p className="mt-2 flex items-start gap-2 text-sm text-[#e9efe7] leading-relaxed" data-testid="studio-interest-done">
                  <CheckCircle2 className="w-5 h-5 shrink-0 text-[#c7a869]" />
                  {t('studio.ctaDone')}
                </p>
              ) : (
                <>
                  <p className="mt-1 text-sm text-[#e9efe7]/90 leading-relaxed">{t('studio.ctaBody')}</p>
                  <Button
                    onClick={signUp}
                    disabled={saving || interested === null}
                    className="mt-4 w-full sm:w-auto bg-[#f7f3ea] text-[#1f4d3a] hover:bg-white"
                    data-testid="studio-interest-button"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {t('studio.ctaButton')}
                  </Button>
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
